import { Injectable } from '@angular/core';
import { Router, Route, UrlSegment } from '@angular/router';
import { GlobalService } from '../services/global.service';
import { StorageService } from '../services/storage.service';

// Types for better type safety
interface ClubDetails {
  user_id: number;
  jwttoken: string;
  club_id?: number;
  username?: string;
  email?: string;
  expires_at?: string;
  [key: string]: any;
}

interface AutoLoginResult {
  canProceed: boolean;
  shouldRedirect: boolean;
  redirectUrl?: string;
  reason: string;
}

// Constants
const AUTO_LOGIN_CONSTANTS = {
  STORAGE_KEY: 'clubDetails',
  AUTHENTICATED_ROUTE: '/app',
  MIN_TOKEN_LENGTH: 10,
  PUBLIC_ROUTES: ['/login', '/registration']
} as const;

@Injectable({
  providedIn: 'root'
})
export class AutoLoginGuard {

  constructor(
    private globalService: GlobalService, 
    private router: Router,
    private storage: StorageService
  ) {}

  async canLoad(route: Route, segments: UrlSegment[]): Promise<boolean> {
    try {
      const intendedUrl = this.buildIntendedUrl(segments);
      console.log('🔐 AutoLoginGuard - Intended URL:', intendedUrl);
      console.log('🔐 AutoLoginGuard - Current URL:', this.router.url);

      const result = await this.performAutoLoginCheck();
      
      return this.handleResult(result, intendedUrl);

    } catch (error) {
      console.error('❌ AutoLoginGuard error:', error);
      this.setUnauthenticated();
      return true; // Allow access on error to prevent blocking
    }
  }

  /**
   * Perform the auto-login check and return structured result
   */
  private async performAutoLoginCheck(): Promise<AutoLoginResult> {
    console.log('🔍 Checking stored authentication...');

    try {
      // Get stored club details
      const clubDetailsResult = await this.storage.getObject<ClubDetails>(AUTO_LOGIN_CONSTANTS.STORAGE_KEY);
      
      if (!clubDetailsResult.success) {
        console.log('⚠️ Failed to retrieve auth data:', clubDetailsResult.error);
        return {
          canProceed: true,
          shouldRedirect: false,
          reason: 'storage_error'
        };
      }

      const clubDetails = clubDetailsResult.value;
      
      if (!clubDetails) {
        console.log('ℹ️ No stored authentication found');
        return {
          canProceed: true,
          shouldRedirect: false,
          reason: 'no_stored_auth'
        };
      }

      console.log('📋 Found stored auth data:', {
        user_id: clubDetails.user_id,
        hasToken: !!clubDetails.jwttoken,
        tokenLength: clubDetails.jwttoken?.length || 0
      });

      // Validate the stored authentication
      const validation = this.validateClubDetails(clubDetails);
      
      if (validation.isValid) {
        console.log('✅ Valid authentication - auto-logging in');
        return {
          canProceed: false, // Don't proceed to original route
          shouldRedirect: true,
          redirectUrl: AUTO_LOGIN_CONSTANTS.AUTHENTICATED_ROUTE,
          reason: 'auto_login_success'
        };
      } else {
        console.log('❌ Invalid authentication:', validation.reason);
        
        // Clear invalid auth data
        await this.clearStoredAuth();
        
        return {
          canProceed: true,
          shouldRedirect: false,
          reason: validation.reason
        };
      }

    } catch (error) {
      console.error('❌ Error during auto-login check:', error);
      throw error;
    }
  }

  /**
   * Validate club details structure and content
   */
  private validateClubDetails(clubDetails: ClubDetails): { isValid: boolean; reason: string } {
    // Check user ID
    if (!clubDetails.user_id || clubDetails.user_id <= 0) {
      return { isValid: false, reason: 'invalid_user_id' };
    }

    // Check JWT token exists
    if (!clubDetails.jwttoken || typeof clubDetails.jwttoken !== 'string') {
      return { isValid: false, reason: 'missing_token' };
    }

    // Check token length (basic validation)
    if (clubDetails.jwttoken.length < AUTO_LOGIN_CONSTANTS.MIN_TOKEN_LENGTH) {
      return { isValid: false, reason: 'token_too_short' };
    }

    // Check token expiry if available
    if (clubDetails.expires_at) {
      try {
        const expiryDate = new Date(clubDetails.expires_at);
        const now = new Date();
        
        if (expiryDate <= now) {
          return { isValid: false, reason: 'token_expired' };
        }
      } catch (error) {
        console.warn('⚠️ Invalid expiry date format:', clubDetails.expires_at);
      }
    }

    return { isValid: true, reason: 'valid' };
  }

  /**
   * Handle the auto-login result and determine what to do
   */
  private handleResult(result: AutoLoginResult, intendedUrl: string): boolean {
    console.log('📊 AutoLogin result:', result);

    if (result.shouldRedirect && result.redirectUrl) {
      // Valid auth found - set authenticated and redirect
      this.setAuthenticated();
      this.navigateToApp(result.redirectUrl);
      return false; // Block original navigation
    }

    if (result.canProceed) {
      // No valid auth or public route - set unauthenticated and allow
      this.setUnauthenticated();
      return true; // Allow original navigation
    }

    // Default case - block navigation
    this.setUnauthenticated();
    return false;
  }

  /**
   * Navigate to authenticated app area
   */
  private navigateToApp(url: string): void {
    console.log('🔄 Redirecting to authenticated area:', url);
    this.router.navigateByUrl(url, { replaceUrl: true }).catch(error => {
      console.error('❌ Navigation error:', error);
    });
  }

  /**
   * Set user as authenticated
   */
  private setAuthenticated(): void {
    console.log('✅ Setting user as authenticated');
    this.globalService.isAuthenticated.next(true);
  }

  /**
   * Set user as unauthenticated
   */
  private setUnauthenticated(): void {
    console.log('❌ Setting user as unauthenticated');
    this.globalService.isAuthenticated.next(false);
  }

  /**
   * Clear stored authentication data
   */
  private async clearStoredAuth(): Promise<void> {
    try {
      const result = await this.storage.removeItem(AUTO_LOGIN_CONSTANTS.STORAGE_KEY);
      if (result.success) {
        console.log('🧹 Cleared invalid stored auth data');
      } else {
        console.warn('⚠️ Failed to clear stored auth:', result.error);
      }
    } catch (error) {
      console.error('❌ Error clearing stored auth:', error);
    }
  }

  /**
   * Build intended URL from segments
   */
  private buildIntendedUrl(segments: UrlSegment[]): string {
    if (!segments || segments.length == 0) {
      return '/';
    }
    return '/' + segments.map(segment => segment.path).join('/');
  }

  /**
   * Check if route is public (doesn't require auth)
   */
  private isPublicRoute(url: string): boolean {
    return AUTO_LOGIN_CONSTANTS.PUBLIC_ROUTES.some(route => 
      url.startsWith(route)
    );
  }

  /**
   * Get current authentication status
   */
  public isAuthenticated(): boolean {
    return this.globalService.isAuthenticated.getValue();
  }

  /**
   * Manual logout helper
   */
  public async logout(): Promise<void> {
    try {
      await this.clearStoredAuth();
      this.setUnauthenticated();
      console.log('👋 User logged out');
    } catch (error) {
      console.error('❌ Error during logout:', error);
    }
  }

  /**
   * Refresh authentication status from storage
   */
  public async refreshAuth(): Promise<boolean> {
    try {
      const result = await this.performAutoLoginCheck();
      
      if (result.shouldRedirect) {
        this.setAuthenticated();
        return true;
      } else {
        this.setUnauthenticated();
        return false;
      }
    } catch (error) {
      console.error('❌ Error refreshing auth:', error);
      this.setUnauthenticated();
      return false;
    }
  }
}